import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { GetProjectIdService } from '../../getProjectId.service';
import { environment } from '../../../environments/environment';
@Injectable({
  providedIn: 'root',
})
export class RequestForQuotationService {
  private apiUrl = environment.apiUrl;
  private subscription: Subscription = new Subscription();
  constructor(
    private http: HttpClient,
    private getProjectIdService: GetProjectIdService
  ) {}

  private projectIdentifierSubject = new BehaviorSubject<string | null>(null);
  projectIdentifier$ = this.projectIdentifierSubject.asObservable();

  setProjectIdentifier(projectIdentifier: string): void {
    this.projectIdentifierSubject.next(projectIdentifier);
  }

  getProjectIdentifier(): string | null {
    return this.projectIdentifierSubject.getValue();
  }

  private rfqIdSubject = new BehaviorSubject<number | null>(null); // Initial value is null
  rfqId$ = this.rfqIdSubject.asObservable(); // Observable for other components to subscribe

  // Set RFQ id
  setRFQId(rfqId: any): void {
    this.rfqIdSubject.next(rfqId);
  }
  // Get the current value of PQQ/RFP id
  getRFQId(): number | null {
    return this.rfqIdSubject.getValue();
  }

  private isEditSubject = new BehaviorSubject<boolean>(false);
  isEdit$ = this.isEditSubject.asObservable();
  // Method to toggle the `isEdit` state
  toggleEdit(): void {
    const currentState = this.isEditSubject.value;
    this.isEditSubject.next(!currentState);
  }

  // Method to set the `isEdit` state directly
  setEditState(state: boolean): void {
    this.isEditSubject.next(state);
  }

  /**
   * ##################################
   * RFQ Developer Start
   * ##################################
   */

  getRFQDeveloperListing(projectId: number): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/listing`
    );
  }
  storeDeveloperRFQWorkPackagesData(
    data: any,
    projectId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/store-work-packages-details`,
      data
    );
  }

  storeDeveloperRfqOrganizationDetailsData(
    data: any,
    projectId: number
  ): Observable<any> {
    const rfqId = this.getRFQId();

    return this.http.put(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/store-organization-details`,
      data
    );
  }

  storeDeveloperRFQSubmissionGuidelinesData(
    data: any,
    projectId: number
  ): Observable<any> {
    // TODO: get rfq id in component and send here
    const rfqId = this.getRFQId();

    return this.http.put(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/store-submission-details`,
      data
    );
  }

  storeDeveloperRFQContractData(data: any, projectId: number): Observable<any> {
    const rfqId = this.getRFQId();

    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/store-contract-details`,
      data
    );
  }
  storeDeveloperRFQKeyDatesData(data: any, projectId: number): Observable<any> {
    const rfqId = this.getRFQId();
    return this.http.put(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/store-dates`,
      data
    );
  }

  submittedInitiationData(projectId: number | string, ittId: number | string) {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${ittId}`
    );
  }

  checkRfqResponseStatus(
    projectId: number,
    rfqId: number,
    rfqResponseId: number
  ): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/response/${rfqResponseId}/submitted`
    );
  }

  initiateRFQ(
    projectId: number | string,
    modelId: number | string
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${modelId}/initiate`,
      {}
    );
  }
  sendRfqForApproval(
    data: any,
    projectId: number,
    modelType: string,
    modelId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/${modelType}/${modelId}/internal-approval/initiate`,
      data
    );
  }

  sendRfqApprovalResponse(
    data: any,
    projectId: number,
    modelType: string,
    modelId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/${modelType}/${modelId}/internal-approval/store-response`,
      data
    );
  }
  /**
   * ##################################
   * RFQ Developer End
   * ##################################
   */

  /**
   * ##################################
   * RFQ Applicant Start
   * ##################################
   */
  private tenderTypeSubject = new BehaviorSubject<string | null>(null);
  tenderType$ = this.tenderTypeSubject.asObservable();

  setTenderType(tenderType: string): void {
    this.tenderTypeSubject.next(tenderType);
  }

  getTenderType(): string | null {
    return this.tenderTypeSubject.getValue();
  }
  fetchRfqTendererListing(): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/requests`
    );
  }
  addRfqConsultancyService(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const rfqId = this.getRFQId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/response/store-consultancy-services-details`,
      data
    );
  }
  addRfqConstructionWork(data: any): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    const rfqId = this.getRFQId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/response/store-construction-works-details`,
      data
    );
  }
  addRfqApplicantTechnicalSubmissionDetails(
    data: any,
    rfqId: number
  ): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/response/store-rfq-response`,
      data
    );
  }

  addRfqCommercialSubmission(data: any, rfqId: number): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/response/store-commercial-submission`,
      data
    );
  }

  addRfqConclusion(data: any, rfqId: number): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/response/store-conclusion-details`,
      data
    );
  }
  getRfqTendererResponse(rfq: number): Observable<any> {
    const projectId = this.getProjectIdService.getProjectId();
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfq}/applicant/response`
    );
  }
  updateRfqTendererResponseStatus(
    projectId: number,
    rfqId: number,
    rfqResponseId: number
  ): Observable<any> {
    console.log('The rfq id is', rfqId);
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/response/${rfqResponseId}/submit`,
      {}
    );
  }
  rfqDeveloperDataWithStep(projectId: number, rfq: number) {
    return this.http.get(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfq}/current-stage`
    );
  }

  rfqAwardContractToApplicantOrganization(
    projectId: number,
    rfqId: number,
    rfqResponseId: number
  ) {
    return this.http.post(
      `${this.apiUrl}/projects/${projectId}/tendering/rfq/${rfqId}/award/contract/${rfqResponseId}`,
      {}
    );
  }

  /**
   * ##################################
   * RFQ Applicant End
   * ##################################
   */
}
